import { useState, useRef, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { classNames } from '../utils/helpers';

export function LoginPage() {
  const { login, adminLogin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const from = (location.state as { from?: Location })?.from?.pathname || '/gallery';

  useEffect(() => {
    passwordInputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (isAdminLogin) {
        await adminLogin(password);
      } else {
        await login({ password, displayName: displayName || undefined });
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setIsAdminLogin(prev => !prev);
    setError(null);
    setPassword('');
    setDisplayName('');
    setTimeout(() => passwordInputRef.current?.focus(), 0);
  };

  return (
    <div className="login-page">
      <main className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">WOCHENENDE 2026</h1>
            <p className="login-subtitle">Unsere Momente. Alle Fotos an einem Ort. Gemeinsam gesammelt.</p>
          </div>

          <div className="login-mode-toggle" role="tablist" aria-label="Anmeldemodus">
            <button
              role="tab"
              aria-selected={!isAdminLogin}
              aria-controls="user-login"
              className={classNames('login-mode-btn', { 'login-mode-btn--active': !isAdminLogin })}
              onClick={toggleMode}
            >
              Galerie-Zugang
            </button>
            <button
              role="tab"
              aria-selected={isAdminLogin}
              aria-controls="admin-login"
              className={classNames('login-mode-btn', { 'login-mode-btn--active': isAdminLogin })}
              onClick={toggleMode}
            >
              Admin-Zugang
            </button>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {error && (
              <div className="login-error" role="alert">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {error}
              </div>
            )}

            {!isAdminLogin && (
              <div className="login-field">
                <label htmlFor="display-name" className="label">Dein Name (optional)</label>
                <input
                  id="display-name"
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Max Mustermann"
                  maxLength={50}
                  className="input"
                  autoComplete="off"
                />
              </div>
            )}

            <div className="login-field">
              <label htmlFor="password" className="label">Passwort</label>
              <input
                ref={passwordInputRef}
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isAdminLogin ? 'Admin-Passwort' : 'Gemeinsames Passwort'}
                className="input"
                autoComplete={isAdminLogin ? 'current-password' : 'off'}
                required
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg login-submit"
              disabled={isSubmitting || !password.trim() || authLoading}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner spinner-sm"></span>
                  Anmelden...
                </>
              ) : (
                isAdminLogin ? 'Admin anmelden' : 'Zur Galerie'
              )}
            </button>
          </form>

          <div className="login-footer">
            <p className="login-hint">
              {isAdminLogin
                ? 'Nur für Administratoren. Passwort nicht weitergeben.'
                : 'Das Passwort wurde dir von den Veranstaltern mitgeteilt.'}
            </p>
          </div>
        </div>

        <p className="login-privacy">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
          Diese Galerie ist privat. Personen mit Zugang können Fotos herunterladen und weitergeben.
          Bitte lade nur Bilder hoch, deren Veröffentlichung innerhalb dieser Gruppe in Ordnung ist.
        </p>
      </main>
    </div>
  );
}